import { t } from "@lingui/macro";
import {
  ContractOrderByColumnEnum,
  ContractsWhereColumn,
  ContractTypeEnum,
  ExternalLinkType,
  OrderOrderByColumnEnum,
  OrdersWhereColumn,
  OtherDocumentOrderByColumnEnum,
  OtherDocumentsWhereColumn,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/urql-graphql";
import type { IOption } from "@src/components/ui-kit";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { contractorOptionsQuery } from "@src/utils/components/filters/paginatedOptions";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { camelCase, lowerCase, map } from "lodash";
import { ExternalLinkColumns } from "./StoreDocuments";

export interface IMixedWhere {
  contracts: Filters<ContractsWhereColumn>;
  orders: Filters<OrdersWhereColumn>;
  otherDocuments: Filters<OtherDocumentsWhereColumn>;
  extLinks: Filters<ExternalLinkColumns>;
}

export interface IMixedOrderBy {
  contracts: OrderBy<ContractOrderByColumnEnum>;
  orders: OrderBy<OrderOrderByColumnEnum>;
  otherDocuments: OrderBy<OtherDocumentOrderByColumnEnum>;
  extLinks: OrderBy<ExternalLinkColumns>;
}

export const getExternalLinksRelatesToOptions = (): IOption[] => [
  { value: ExternalLinkType.Project, label: t`Project` },
  { value: ExternalLinkType.Brand, label: t`Brand` },
];

export abstract class BaseMixedFilters {
  where: IMixedWhere;
  orderBy: IMixedOrderBy;
  pagination: PaginationState;

  constructor() {
    this.pagination = new PaginationState("mixedDocuments");

    this.where = {
      contracts: new Filters<ContractsWhereColumn>(
        [
          new Filter({
            column: ContractsWhereColumn.BrandId,
            title: t`Brand`,
            isMultiColumn: true,
            multiColumn: {
              label: t`Related to`,
              type: "tabs",
            },
            options: [],
            operator: SqlOperator.In,
          }),
          new Filter({
            column: ContractsWhereColumn.ClientId,
            title: t`Client`,
            isMultiColumn: true,
            multiColumn: {
              label: t`Related to`,
              type: "tabs",
            },
            options: [],
            operator: SqlOperator.In,
          }),
          new Filter({
            column: ContractsWhereColumn.ProjectId,
            title: t`Project`,
            isMultiColumn: true,
            multiColumn: {
              label: t`Related to`,
              type: "tabs",
            },
            options: [],
            operator: SqlOperator.In,
          }),
          new Filter({
            column: ContractsWhereColumn.ContractorId,
            title: t`Contractor`,
            options: [],
            operator: SqlOperator.In,
            isPaginated: true,
            optionsQuery: contractorOptionsQuery,
          }),
          new Filter({
            column: ContractsWhereColumn.Type,
            title: t`Type`,
            options: map(ContractTypeEnum, (f) => ({
              label: lowerCase(f),
              value: camelCase(f),
            })),
            operator: SqlOperator.In,
          }),
          new Filter({
            column: ContractsWhereColumn.ValidFrom,
            title: t`Validity (from)`,
            options: [],
            dateRange: true,
            operator: SqlOperator.Between,
          }),
          new Filter({
            column: ContractsWhereColumn.ValidTo,
            title: t`Validity (to)`,
            options: [],
            dateRange: true,
            operator: SqlOperator.Between,
          }),
        ],
        {
          onChange: () => {
            this.pagination.currentPage = 1;
          },
          tableKey: "contracts",
        },
      ),
      orders: new Filters<OrdersWhereColumn>(
        [
          new Filter({
            column: OrdersWhereColumn.BrandId,
            operator: SqlOperator.In,
            title: t`Brand`,
            options: [],
          }),
          new Filter({
            column: OrdersWhereColumn.ProjectId,
            operator: SqlOperator.In,
            title: t`Project`,
            options: [],
          }),
          new Filter({
            column: OrdersWhereColumn.DateOfOrder,
            title: t`Date of order`,
            options: [],
            date: true,
            operator: SqlOperator.Eq,
          }),
        ],
        {
          onChange: () => {
            this.pagination.currentPage = 1;
          },
          tableKey: "orders",
        },
      ),
      otherDocuments: new Filters<OtherDocumentsWhereColumn>(
        [
          new Filter({
            column: OtherDocumentsWhereColumn.BrandId,
            title: t`Brand`,
            isMultiColumn: true,
            multiColumn: {
              label: t`Related to`,
              type: "tabs",
            },
            options: [],
            operator: SqlOperator.In,
          }),
          new Filter({
            column: OtherDocumentsWhereColumn.ProjectId,
            title: t`Project`,
            isMultiColumn: true,
            multiColumn: {
              label: t`Related to`,
              type: "tabs",
            },
            options: [],
            operator: SqlOperator.In,
          }),
          new Filter({
            column: OtherDocumentsWhereColumn.ClientId,
            title: t`Project`,
            isMultiColumn: true,
            multiColumn: {
              label: t`Related to`,
              type: "tabs",
            },
            options: [],
            operator: SqlOperator.In,
          }),
          new Filter({
            column: OtherDocumentsWhereColumn.ContractorId,
            title: t`Contractor`,
            options: [],
            operator: SqlOperator.In,
            isPaginated: true,
            optionsQuery: contractorOptionsQuery,
          }),
        ],
        { onChange: this.pagination.resetPage, tableKey: "other-documents" },
      ),
      extLinks: new Filters<ExternalLinkColumns>(
        [
          new Filter({
            column: ExternalLinkColumns.ProjectId,
            operator: SqlOperator.In,
            title: t`Project`,
            options: [],
          }),
          new Filter({
            column: ExternalLinkColumns.BrandId,
            operator: SqlOperator.In,
            title: t`Brand`,
            options: [],
          }),
          new Filter({
            column: ExternalLinkColumns.ModelType,
            operator: SqlOperator.In,
            title: t`Relates to`,
            options: getExternalLinksRelatesToOptions(),
          }),
        ],
        { tableKey: "external-links" },
      ),
    };

    this.orderBy = {
      contracts: new OrderBy<ContractOrderByColumnEnum>([
        { column: ContractOrderByColumnEnum.Title, order: SortOrder.Asc },
      ]),
      orders: new OrderBy<OrderOrderByColumnEnum>([
        {
          column: OrderOrderByColumnEnum.PurchaseOrderNo,
          order: SortOrder.Asc,
        },
      ]),
      otherDocuments: new OrderBy<OtherDocumentOrderByColumnEnum>([
        { column: OtherDocumentOrderByColumnEnum.Title, order: SortOrder.Asc },
      ]),
      extLinks: new OrderBy<ExternalLinkColumns>([]),
    };
  }
}

export class MixedFilters extends BaseMixedFilters {}
